// Mock data for SuperHunt.ai website

export const siteConfig = {
  name: "SuperHunt",
  tagline: "Access the People Who Matter",
  logo: "https://customer-assets.emergentagent.com/job_d7eb6b3f-f937-4d5e-8280-d403640a7122/artifacts/9lh8g08o_SuperHunt%20Logo%20v1.png",
  favicon: "https://customer-assets.emergentagent.com/job_d7eb6b3f-f937-4d5e-8280-d403640a7122/artifacts/vdxv0syh_SuperHunt%20Logo%20Favicon.png",
  calendlyUrl: "https://calendly.com/superhunt/45min",
  appLoginUrl: "https://app.superhunt.ai/login",
  linkedinUrl: "https://www.linkedin.com/company/superhunt-ai/"
};

export const heroContent = {
  tagline: "Ally of the LinkedIn Hunter",
  headline: "Access the People Who Matter",
  subheadline: "Close roles and deals faster with an AI co-pilot for LinkedIn sourcing and outreach",
  ctaText: "See SuperHunt.ai in Action"
};

export const problemsData = [
  {
    id: 1,
    icon: "Search",
    title: "Fragmented Discovery",
    description: "LinkedIn visibility doesn't equal direct access. Profiles are visible, but verified contact information remains scattered across multiple tools and platforms.",
    impact: "Hours lost jumping between tools"
  },
  {
    id: 2,
    icon: "Database",
    title: "Data Decay & Unreliability",
    description: "Phone numbers and emails become outdated quickly. Contact databases deteriorate faster than they can be refreshed, leading to missed connections.",
    impact: "30% of contacts go stale monthly"
  },
  {
    id: 3,
    icon: "MessageSquare",
    title: "Siloed Outreach Channels",
    description: "LinkedIn, Email, and WhatsApp campaigns run in isolation. Without coordination, you lose momentum and risk appearing disorganized to prospects.",
    impact: "Conversations start late, momentum lost"
  },
  {
    id: 4,
    icon: "Clock",
    title: "Time vs Quality Tradeoff",
    description: "Scaling outreach either burns precious time or dilutes quality. Manual processes create redundancy, fatigue, and inevitable misses.",
    impact: "4-6 hours per role on manual hunting"
  }
];

export const processStages = [
  {
    id: 1,
    stage: "Stage 1",
    title: "AI-Led Audience Discovery",
    duration: "10-12 mins",
    description: "Create intelligent search variations, collect and deduplicate profile URLs, and gather comprehensive profile data—all automated with minimal input.",
    features: [
      "Smart keyword expansion",
      "Automated profile scraping",
      "Intelligent deduplication",
      "Stack ranking by 5 key parameters"
    ],
    manualTime: "2-4 hours manually",
    icon: "Target",
    image: "https://customer-assets.emergentagent.com/job_hunt-smarter/artifacts/kuzal852_Step%201.png"
  },
  {
    id: 2,
    stage: "Stage 2",
    title: "Verified Contact Finding",
    duration: "5-7 mins",
    description: "Get up to 70% verified direct mobile numbers and personal emails with a single click. Review and approve the AI-ranked profiles with human judgement.",
    features: [
      "70% contact success rate",
      "Direct mobile numbers",
      "Personal email verification",
      "Human-in-loop review"
    ],
    manualTime: "Hours of laborious searching",
    icon: "UserCheck",
    image: "https://customer-assets.emergentagent.com/job_hunt-smarter/artifacts/bnjgkmfa_Step%202.png"
  },
  {
    id: 3,
    stage: "Stage 3",
    title: "Multi-Channel Outreach Setup",
    duration: "Fully Automated",
    description: "Launch coordinated campaigns across LinkedIn, WhatsApp, and Email. Multi-message sequences run automatically, turning cold outreach into warm conversations.",
    features: [
      "LinkedIn InMail sequences",
      "WhatsApp campaigns",
      "Email drip sequences",
      "Campaign performance tracking"
    ],
    manualTime: "Days of follow-ups",
    icon: "Send",
    image: "https://customer-assets.emergentagent.com/job_hunt-smarter/artifacts/h13mapxu_Step%203.png"
  }
];

export const brandIdentity = {
  logoName: "The Geometric Falcon",
  philosophy: "Our logo isn't illustrated—it's engineered. Built entirely from interlocking geometric forms, each facet represents a tool, a signal, or a step in a larger workflow. Just like SuperHunt orchestrates multiple systems into one decisive motion.",
  principles: [
    {
      title: "Precision Over Volume",
      description: "We believe in purposeful pursuit—making every move count, operating with intent rather than reacting to noise."
    },
    {
      title: "Speed Without Waste",
      description: "Forward motion and momentum without visual clutter or unnecessary steps. Intentional, measured, and aligned to outcomes."
    },
    {
      title: "Confidence Without Aggression",
      description: "A composed form that reflects professional authority while reinforcing respect for relationships."
    }
  ]
};

export const teamMembers = [
  {
    id: 1,
    name: "Vinay Murthy",
    role: "Founder & CEO",
    linkedin: "https://www.linkedin.com/in/vbsmurthy/",
    image: "/images/team/Vinay.jpg",
    grayscale: true
  },
  {
    id: 2,
    name: "Ashwini S",
    role: "Director",
    linkedin: "https://www.linkedin.com/in/ashwini-sheshagiri-41506923/",
    image: "/images/team/Ashwini.png",
    grayscale: true
  },
  {
    id: 3,
    name: "Rashmi K",
    role: "Delivery Manager",
    linkedin: "https://www.linkedin.com/in/rashmi-deepak-236a6b43/",
    image: "/images/team/Rashmi.png",
    grayscale: true
  },
  {
    id: 4,
    name: "Surendra Singh",
    role: "CTO & Product Leader",
    linkedin: "https://www.linkedin.com/in/surendrasinghs/",
    image: "https://customer-assets.emergentagent.com/job_hunt-smarter/artifacts/zx9pje2o_Suren.jfif",
    grayscale: true
  },
  {
    id: 5,
    name: "Anupama",
    role: "Delivery Lead",
    linkedin: "https://www.linkedin.com/in/anupama-nadig-b0b626138/",
    image: "/images/team/Anupama.jfif",
    grayscale: true
  },
  {
    id: 6,
    name: "Varun Kumar Shekar",
    role: "Product Lead",
    linkedin: "https://www.linkedin.com/in/varun-kumar-shekar/",
    image: "https://customer-assets.emergentagent.com/job_hunt-smarter/artifacts/zq7jxr2x_Varun.png",
    grayscale: true
  },
  {
    id: 7,
    name: "Gokul K Anil",
    role: "Engineering Lead",
    linkedin: "https://www.linkedin.com/in/gokul-k-anil/",
    image: "https://customer-assets.emergentagent.com/job_hunt-smarter/artifacts/350vckj3_Gokul.jfif",
    grayscale: true
  },
  {
    id: 8,
    name: "Madhavi Chamarthi",
    role: "Recruiting Lead",
    linkedin: "https://www.linkedin.com/in/madhavi-chamarthi-337848231/",
    image: "https://customer-assets.emergentagent.com/job_hunt-smarter/artifacts/a9q7o89o_Madhavi.jfif",
    grayscale: true
  }
];

export const clientLogos = [
  { id: 1, name: "Aligned Automation", image: "/company-logos/aligned-automation.jfif" },
  { id: 2, name: "Channel Bridge", image: "/company-logos/channel-bridge.png" },
  { id: 3, name: "Chimera", image: "/company-logos/chimera-logo.png" },
  { id: 4, name: "Digit Insurance", image: "/company-logos/digitinsurance.png" },
  { id: 5, name: "GoQii", image: "/company-logos/goqii-logo.png" },
  { id: 6, name: "Gracenote", image: "/company-logos/gracenote.png" },
  { id: 7, name: "Lamdazen", image: "/company-logos/lamdazen.jfif" },
  { id: 8, name: "Lumen", image: "/company-logos/lumen.png" },
  { id: 9, name: "Microland", image: "/company-logos/microland-logo.jpg" },
  { id: 10, name: "Nielson", image: "/company-logos/nielson.jfif" },
  { id: 11, name: "Sheshi", image: "/company-logos/sheshi.jfif" },
  { id: 12, name: "Socialswag", image: "/company-logos/socialswag.jfif" },
  { id: 13, name: "Spur Tree", image: "/company-logos/spur-tree-logo.png" },
  { id: 14, name: "Treez", image: "/company-logos/treez-logo.jpg" },
  { id: 15, name: "Zeta", image: "/company-logos/zeta.png" }
];

export const faqData = [
  {
    id: 1,
    question: "Who should be using SuperHunt.ai?",
    answer: "SuperHunt is designed for both executive recruiters doing passive sourcing for tech, sales, and leadership talent, AND sales professionals who hunt on LinkedIn for prospective clients. Our platform speaks to anyone who needs to access the people who matter—whether you're filling a critical role or closing a strategic deal."
  },
  {
    id: 2,
    question: "Does Passive Sourcing in 30 mins really work with SuperHunt?",
    answer: "Absolutely. Our 20+ automated steps are organized into 3 focused stages: AI-led Audience Discovery (10-12 mins), Verified Contact Finding (5-7 mins), and Multi-Channel Outreach Setup (fully automated). The entire process can be completed in under 30 minutes. This doesn't include manual review of profiles—which is the 'Human in Loop' step you can do on the platform for quality control."
  },
  {
    id: 3,
    question: "How can I go from Cold Outreach to Warm Leads with SuperHunt?",
    answer: "It's a proven sequence: Create an AI-assisted targeted audience of around 200 profiles in just 10-12 minutes. Get their verified contact details in 5-7 minutes. Then set up coordinated multi-channel outreach across LinkedIn, WhatsApp, and Email. This orchestrated approach transforms cold outreach into warm conversations—turning prospects into clients and candidates into hires."
  },
  {
    id: 4,
    question: "What is the Pricing & SaaS Package at SuperHunt.ai?",
    answer: "SuperHunt costs just as much as an entry-level recruiter or sales professional—₹33,333 per month per user. This includes 5,000 outreach messages, 2,500 LinkedIn prospects, and 1,000 verified contacts. We can customize packages for bulk or annual subscriptions based on your team's needs."
  },
  {
    id: 5,
    question: "Are there exciting features coming next, like Voice AI?",
    answer: "You hit the nail on the head! Voice AI for pre-qualifying and warm-up calls is next on our roadmap. We're also building: Visuals with GenAI for qualified visual job descriptions and compelling image/video message templates for multi-channel campaigns or Meta Ads. Bulk data access is coming too. Finally, globalization—so you can do sales and recruiting across all English-speaking countries from the US and UK to 20+ nations."
  }
];

export const contactInfo = {
  phone: "+91 9945325633",
  email: "hello@superhunt.ai",
  address: "Garuda BHIVE Workspace, BMTC Complex, Outer Ring Rd, BTM 2nd Stage, BTM Layout, Bengaluru, Karnataka 560076",
  mapLink: "https://maps.app.goo.gl/EXXgPxxSFZzwNmk28"
};

export const statsData = [
  { value: "100+", label: "Clients Served" },
  { value: "3000+", label: "Successful Placements" },
  { value: "80%", label: "Time Saved" },
  { value: "70%", label: "Contact Success Rate" }
];
